import { onBeforeUnmount, onMounted } from 'vue'

/**
 * Keyboard glue for ui-lib `UIDropdown` popovers (and `UIInputSelect`, which is
 * built on it), installed once for the whole app from `App.vue`.
 *
 * UPSTREAM CANDIDATE: both gaps below belong in ui-lib's `UIDropdown`; delete this
 * file once it ships them.
 *
 * 1. Escape. ui-lib modals dismiss on an Escape listener on `document`, but a
 *    `UIDropdown` only handles Escape while focus is *inside its list*. Right after
 *    a click or Enter the focus is still on the trigger, so Escape bubbles up and
 *    closes the surrounding modal along with (or instead of) the popover. A
 *    capture-phase `window` listener runs before both: while a popover is open it
 *    closes only the topmost popover and stops the event, so Escape closes the
 *    topmost layer only. With nothing open, or with focus already in the option list (which ui-lib
 *    handles itself), the event is left alone and the modal dismisses as usual.
 *
 * 2. Opening from the keyboard. `UIDropdown` opens on Enter and click only. The
 *    trigger also opens on Space and ArrowDown here, as a native select does.
 *
 * There is no public handle on a dropdown's open state or a `close()` method, so
 * this relies on ui-lib's markup (as the rest of the app already does for styling):
 * the panel is `.UIDropdown__content` and exists only while open; the default toggle
 * carries `data-open`; the panel closes on any click outside its trigger and panel,
 * which is what is dispatched to close it.
 */

const OPEN_PANEL = '.UIDropdown__content'
const LIST = '.UIDropdown__content-list'
const TRIGGER = '.UIDropdown__trigger'
const DEFAULT_TOGGLE = '.UIDropdownToggle'

let installedCount = 0
let lastTrigger: HTMLElement | null = null

const FOCUSABLE = 'button, [href], input, [tabindex]:not([tabindex="-1"])'

const isOpen = () => document.querySelector(OPEN_PANEL) !== null

/**
 * Remembers the control that opened a popover, to hand focus back on Escape. Runs on
 * `focusin` and on `click`, because a trigger that does not take focus on click
 * (Safari buttons, a custom trigger) never fires `focusin`, which would leave the
 * previous popover's trigger as the one remembered.
 */
const rememberTrigger = (event: Event) => {
  const target = event.target
  // An Element, not an HTMLElement: a click on a button's icon lands on an SVG node.
  if (!(target instanceof Element)) return
  const trigger = target.closest<HTMLElement>(TRIGGER)
  if (!trigger) return
  const control = target.matches(FOCUSABLE)
    ? (target as HTMLElement)
    : trigger.querySelector<HTMLElement>(FOCUSABLE)
  if (control) lastTrigger = control
}

const restoreTriggerFocus = () => {
  const trigger = lastTrigger
  lastTrigger = null
  requestAnimationFrame(() => {
    if (
      trigger?.isConnected &&
      (document.activeElement === document.body || !document.activeElement)
    ) {
      trigger.focus({ preventScroll: true })
    }
  })
}

// ui-lib returns focus to its default toggle on every close, including a click
// outside that has just focused a field: hand focus back to that field.
const keepOutsideFocus = (event: Event) => {
  const target = event.target
  if (!isOpen() || !(target instanceof Element)) return
  if (target.closest(OPEN_PANEL)) return
  const focused = document.activeElement
  if (!(focused instanceof HTMLElement) || focused === document.body) return
  requestAnimationFrame(() => {
    if (
      focused.isConnected &&
      document.activeElement?.matches(DEFAULT_TOGGLE)
    ) {
      focused.focus({ preventScroll: true })
    }
  })
}

const onKeydown = (event: KeyboardEvent) => {
  // Enter / Space reopening from a trigger that kept focus fires no `focusin`.
  if (event.key !== 'Escape') rememberTrigger(event)
  if (event.key === 'Escape') {
    // Panels are teleported to <body> in the order they open, so the last one in
    // document order is the topmost (a submenu opens after its parent).
    const openPanels = document.querySelectorAll<HTMLElement>(OPEN_PANEL)
    const topmost = openPanels[openPanels.length - 1]
    if (!topmost) return
    // Focus inside the topmost panel's own list: ui-lib's list handler closes that
    // panel and stops the event, which keeps a submenu's Escape from closing its
    // parent as well. Custom panel content (`content-container`, e.g. a form) has no
    // such handler, so Escape there is closed here.
    if (
      event.target instanceof Element &&
      event.target.closest(LIST) &&
      topmost.contains(event.target)
    ) {
      if (openPanels.length === 1) restoreTriggerFocus()
      return
    }

    event.stopPropagation()
    event.preventDefault()
    // A click inside the panel underneath is "outside" only for the topmost panel, so
    // a submenu closes and its parent stays open; with one panel open, a click on the
    // body closes it.
    const clickTarget =
      openPanels.length > 1 ? openPanels[openPanels.length - 2] : document.body
    clickTarget.dispatchEvent(new MouseEvent('click', { bubbles: true }))
    if (openPanels.length > 1) return // focus stays in or on the parent

    // ui-lib hands focus back to its default toggle; a custom trigger loses it when
    // the focused row is removed with the panel.
    restoreTriggerFocus()
    return
  }

  if (event.key !== ' ' && event.key !== 'ArrowDown') return
  if (event.altKey || event.ctrlKey || event.metaKey) return

  const target = event.target
  if (!(target instanceof HTMLElement)) return
  const toggle = target.matches(DEFAULT_TOGGLE) ? target : null
  if (target.getAttribute('aria-disabled') === 'true') return
  // Native buttons already open on Space (via click) and Enter; only the toggle div
  // and a custom trigger's ArrowDown are missing.
  if (!toggle && (event.key === ' ' || !target.closest(TRIGGER))) return
  if (toggle && !toggle.closest(TRIGGER)) return
  if (toggle ? toggle.dataset.open === 'true' : isOpen()) return

  event.preventDefault()
  target.click()
}

export function usePopoverEscapeGuard() {
  onMounted(() => {
    if (installedCount++ === 0) {
      window.addEventListener('keydown', onKeydown, true)
      window.addEventListener('focusin', rememberTrigger, true)
      window.addEventListener('click', rememberTrigger, true)
      window.addEventListener('click', keepOutsideFocus, true)
    }
  })
  onBeforeUnmount(() => {
    if (--installedCount === 0) {
      window.removeEventListener('keydown', onKeydown, true)
      window.removeEventListener('focusin', rememberTrigger, true)
      window.removeEventListener('click', rememberTrigger, true)
      window.removeEventListener('click', keepOutsideFocus, true)
      lastTrigger = null
    }
  })
}
